import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "../context/ToastContext";
import ApiClient from "../utils/api";
import {
  Type,
  FileText,
  Hash,
  ChevronDown,
  CheckSquare,
  CircleDot,
  Calendar,
  Search,
  List,
  Link,
  ChevronUp,
  Settings,
  Trash2,
  Plus,
  ArrowLeft,
  X,
  GripVertical,
  Lock,
  Folder
} from "lucide-react";

// List of all 10 custom field types with their display configurations
const FIELD_TYPES = [
  { id: "text", label: "Text field", icon: Type, iconText: "T_I", desc: "Single line of text" },
  { id: "textarea", label: "Text area", icon: FileText, iconText: "T[..]", desc: "Multi-line text editor" },
  { id: "number", label: "Number", icon: Hash, iconText: "123", desc: "Integers or decimal values" },
  { id: "dropdown", label: "Dropdown", icon: ChevronDown, iconText: "▼", desc: "Select one option from a list" },
  { id: "checkbox", label: "Checkbox", icon: CheckSquare, iconText: "☑", desc: "True/false checkbox option" },
  { id: "radio", label: "Radio button", icon: CircleDot, iconText: "⊙", desc: "Select one option from a list" },
  { id: "date", label: "Date picker", icon: Calendar, iconText: "25", desc: "Date calendar selector" },
  { id: "lookup", label: "Lookup", icon: Search, iconText: "Q≡", desc: "Query references to other records" },
  { id: "multiselect", label: "Multiselect", icon: List, iconText: "▤", desc: "Select multiple options from a list" },
  { id: "url", label: "URL", icon: Link, iconText: "🔗", desc: "Web address chain link" }
];


// Default built-in groups
const DEFAULT_GROUPS = [
  { name: "Basic information", key: "ec_basic_information", parentGroup: null },
  { name: "Telephone numbers", key: "ec_telephone_numbers", parentGroup: "ec_basic_information" }
];

// List of system-defined contact fields
const SYSTEM_FIELDS = [
  { name: "Full name", key: "name", type: "text", isSystem: true, required: true, quick_add: true, placeholder: "e.g. John Doe", tooltip: "The contact's display name", group: "ec_basic_information" },
  { name: "Email", key: "email", type: "text", isSystem: true, required: false, quick_add: true, placeholder: "e.g. john@example.com", tooltip: "Primary contact email address", group: "ec_basic_information" },
  { name: "Mobile", key: "phone", type: "text", isSystem: true, required: false, quick_add: true, placeholder: "e.g. +1 234 567 8900", tooltip: "Primary mobile phone number", group: "ec_telephone_numbers" },
  { name: "Total chat sessions", key: "total_sessions", type: "number", isSystem: true, required: false, quick_add: false, placeholder: "", tooltip: "Total number of chat sessions", group: "ec_basic_information", read_only: true },
  { name: "Created at", key: "created_at", type: "text", isSystem: true, required: false, quick_add: false, placeholder: "", tooltip: "Timestamp of first contact interaction", group: "ec_basic_information", read_only: true },
  { name: "Updated at", key: "updated_at", type: "text", isSystem: true, required: false, quick_add: false, placeholder: "", tooltip: "Timestamp of last contact interaction", group: "ec_basic_information", read_only: true },
  { name: "Language", key: "locale", type: "text", isSystem: true, required: false, quick_add: false, placeholder: "e.g. en_US", tooltip: "The region or language preference of the contact", group: "ec_basic_information" },
  { name: "Time Zone", key: "timezone", type: "text", isSystem: true, required: false, quick_add: false, placeholder: "e.g. GMT+5:30", tooltip: "Local time zone of the contact", group: "ec_basic_information" }
];

export default function ManageFields() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const backRefContactId = searchParams.get("contact_ref");

  const { showToast } = useToast();
  const [fields, setFields] = useState([]);
  const [groups, setGroups] = useState(DEFAULT_GROUPS);
  const [loading, setLoading] = useState(true);

  // Drawer states
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerStep, setDrawerStep] = useState(1); // 1 = Select Grid, 2 = Configure Form
  const [selectedType, setSelectedType] = useState(null); // Selected FIELD_TYPE id
  const [drawerType, setDrawerType] = useState("field"); // "field" or "group"

  // Accordion expanded field definitions
  const [expandedFields, setExpandedFields] = useState({});

  // Group Form states
  const [formGroupId, setFormGroupId] = useState(null); // key if editing
  const [formGroupName, setFormGroupName] = useState("");
  const [formGroupKey, setFormGroupKey] = useState("ec_");
  const [formGroupNest, setFormGroupNest] = useState(false);
  const [formGroupParent, setFormGroupParent] = useState("");

  // Field Form states
  const [formFieldId, setFormFieldId] = useState(null); // if editing
  const [formLabel, setFormLabel] = useState("");
  const [formKey, setFormKey] = useState("");
  const [formTooltip, setFormTooltip] = useState("");
  const [formPlaceholder, setFormPlaceholder] = useState("");
  const [formRequired, setFormRequired] = useState(false);
  const [formQuickAdd, setFormQuickAdd] = useState(true);
  const [formReadOnly, setFormReadOnly] = useState(false);
  const [formUnique, setFormUnique] = useState(false);
  const [formFieldGroup, setFormFieldGroup] = useState("ec_basic_information");

  // Extra Config states for new fields
  const [formNumberFormat, setFormNumberFormat] = useState("integer");
  const [formLookupModule, setFormLookupModule] = useState("Contacts");
  const [formChoices, setFormChoices] = useState([]);
  const [newChoiceText, setNewChoiceText] = useState("");
  const [bulkChoicesText, setBulkChoicesText] = useState("");
  const [showBulkAddModal, setShowBulkAddModal] = useState(false);
  const [choiceSearchQuery, setChoiceSearchQuery] = useState("");


  useEffect(() => {
    fetchGlobalFields();
  }, []);

  const fetchGlobalFields = async () => {
    setLoading(true);
    try {
      const res = await ApiClient.getGlobalCustomFields();
      let rawData = res.fields || [];
      if (rawData && !Array.isArray(rawData) && rawData.fields) {
        setFields(rawData.fields);
        setGroups(rawData.groups || DEFAULT_GROUPS);
      } else {
        setFields(Array.isArray(rawData) ? rawData : []);
        setGroups(DEFAULT_GROUPS);
      }
    } catch (err) {
      console.error("Error loading global custom fields:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddDrawer = (groupKey = "ec_basic_information") => {
    setDrawerType("field");
    setFormFieldId(null);
    setFormLabel("");
    setFormKey("ec_");
    setSelectedType(null);
    setFormTooltip("");
    setFormPlaceholder("Start typing...");
    setFormRequired(false);
    setFormQuickAdd(true);
    setFormReadOnly(false);
    setFormUnique(false);
    setFormFieldGroup(groupKey);
    setFormNumberFormat("integer");
    setFormLookupModule("Contacts");
    setFormChoices([]);
    setNewChoiceText("");
    setBulkChoicesText("");
    setShowBulkAddModal(false);
    setChoiceSearchQuery("");

    setDrawerStep(1);
    setIsDrawerOpen(true);
  };

  const handleOpenEditDrawer = (fd) => {
    setDrawerType("field");
    setFormFieldId(fd.key);
    setFormLabel(fd.name);
    setFormKey(fd.key);
    setSelectedType(fd.type || "text");
    setFormTooltip(fd.tooltip || "");
    setFormPlaceholder(fd.placeholder || "");
    setFormRequired(!!fd.required);
    setFormQuickAdd(!!fd.quick_add);
    setFormReadOnly(!!fd.read_only);
    setFormUnique(!!fd.unique);
    setFormFieldGroup(fd.group || "ec_basic_information");
    setFormNumberFormat(fd.numberFormat || "integer");
    setFormLookupModule(fd.lookupModule || "Contacts");
    setFormChoices(fd.choices || []);
    setNewChoiceText("");
    setBulkChoicesText("");
    setShowBulkAddModal(false);
    setChoiceSearchQuery("");

    setDrawerStep(2); // Go directly to configuration form
    setIsDrawerOpen(true);
  };


  const handleOpenAddGroupDrawer = (parentKey = null) => {
    setDrawerType("group");
    setFormGroupId(null);
    setFormGroupName("");
    setFormGroupKey("ec_");
    setFormGroupNest(!!parentKey);
    setFormGroupParent(parentKey || "");
    setIsDrawerOpen(true);
  };

  const handleOpenEditGroupDrawer = (g) => {
    setDrawerType("group");
    setFormGroupId(g.key);
    setFormGroupName(g.name);
    setFormGroupKey(g.key);
    setFormGroupNest(!!g.parentGroup);
    setFormGroupParent(g.parentGroup || "");
    setIsDrawerOpen(true);
  };

  const handleGroupNameChange = (val) => {
    setFormGroupName(val);
    if (!formGroupId) {
      const sanitized = val.toLowerCase().replace(/[^a-z0-9]/g, "_");
      setFormGroupKey(sanitized ? `ec_${sanitized}` : "ec_");
    }
  };

  const handleSaveGroup = async (e) => {
    e.preventDefault();
    if (!formGroupName.trim()) {
      showToast("Please enter a group name.", "error");
      return;
    }

    let key = formGroupKey.trim();
    if (!key || key === "ec_") {
      key = "ec_" + formGroupName.toLowerCase().replace(/[^a-z0-9]/g, "_");
    }

    if (!formGroupId && groups.some(g => g.key === key)) {
      showToast("A group with this name or key already exists.", "error");
      return;
    }

    const groupData = {
      name: formGroupName.trim(),
      key: key,
      parentGroup: formGroupNest ? (formGroupParent || null) : null
    };

    let updatedGroups;
    let updatedFields = [...fields];
    if (formGroupId) {
      updatedGroups = groups.map(g => g.key === formGroupId ? groupData : g);
      if (formGroupId !== key) {
        updatedGroups = updatedGroups.map(g => g.parentGroup === formGroupId ? { ...g, parentGroup: key } : g);
        updatedFields = fields.map(f => f.group === formGroupId ? { ...f, group: key } : f);
      }
    } else {
      updatedGroups = [...groups, groupData];
    }

    try {
      const payload = { fields: updatedFields, groups: updatedGroups };
      await ApiClient.updateGlobalCustomFields(payload);
      setGroups(updatedGroups);
      setFields(updatedFields);
      setIsDrawerOpen(false);
      showToast("Group saved successfully!", "success");
    } catch (err) {
      console.error("Error saving group:", err);
      showToast("Failed to save group settings.", "error");
    }
  };

  const handleDeleteGroup = async (groupKey) => {
    const isDefault = DEFAULT_GROUPS.some(dg => dg.key === groupKey);
    const msg = isDefault
      ? "Deleting a default system group will move its fields to ungrouped. Are you sure you want to delete this group?"
      : "Are you sure you want to delete this group? All fields inside this group will be moved to the default Basic Information group.";

    if (window.confirm(msg)) {
      const updatedGroups = groups.filter(g => g.key !== groupKey).map(g => g.parentGroup === groupKey ? { ...g, parentGroup: null } : g);
      const updatedFields = fields.map(f => f.group === groupKey ? { ...f, group: "ec_basic_information" } : f);

      try {
        const payload = { fields: updatedFields, groups: updatedGroups };
        await ApiClient.updateGlobalCustomFields(payload);
        setGroups(updatedGroups);
        setFields(updatedFields);
      } catch (err) {
        console.error("Error deleting group:", err);
        alert("Failed to delete group.");
      }
    }
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handleCardSelect = (typeId) => {
    setSelectedType(typeId);
  };

  const handleProceedToForm = () => {
    if (!selectedType) return;
    setDrawerStep(2);
  };

  const handleLabelChange = (val) => {
    setFormLabel(val);
    if (!formFieldId) {
      const sanitized = val.toLowerCase().replace(/[^a-z0-9]/g, "_");
      setFormKey(sanitized ? `ec_${sanitized}` : "ec_");
    }
  };

  const handleSaveField = async (e) => {
    e.preventDefault();
    if (!formLabel.trim()) {
      showToast("Please enter a field name.", "error");
      return;
    }

    let key = formKey.trim();
    if (!key || key === "ec_") {
      key = "ec_" + formLabel.toLowerCase().replace(/[^a-z0-9]/g, "_");
    }

    if (!formFieldId && fields.some(f => f.key === key)) {
      showToast("A field with this name or key already exists.", "error");
      return;
    }

    const isSystemField = SYSTEM_FIELDS.some(sf => sf.key === key);
    const fieldData = {
      name: formLabel.trim(),
      key: key,
      type: selectedType || "text",
      tooltip: formTooltip.trim(),
      placeholder: formPlaceholder.trim(),
      required: formRequired,
      quick_add: formQuickAdd,
      read_only: formReadOnly,
      unique: formUnique,
      isSystem: isSystemField,
      group: formFieldGroup || "ec_basic_information",
      numberFormat: formNumberFormat,
      lookupModule: formLookupModule,
      choices: formChoices
    };


    let updatedFields;
    const existsInFields = fields.some(f => f.key === key || (formFieldId && f.key === formFieldId));
    if (existsInFields) {
      updatedFields = fields.map(f => f.key === (formFieldId || key) ? fieldData : f);
    } else {
      updatedFields = [...fields, fieldData];
    }

    try {
      const payload = { fields: updatedFields, groups };
      await ApiClient.updateGlobalCustomFields(payload);
      setFields(updatedFields);
      setIsDrawerOpen(false);
      showToast("Custom field saved successfully!", "success");
    } catch (err) {
      console.error("Error saving global custom field:", err);
      showToast("Failed to save field settings.", "error");
    }
  };

  const handleDeleteField = async (fieldKey) => {
    const isSystemField = SYSTEM_FIELDS.some(sf => sf.key === fieldKey);
    const confirmMsg = isSystemField
      ? "Are you sure you want to delete this system field? It will be hidden across all contacts."
      : "Are you sure you want to delete this custom field globally? It will be removed from display across all contacts.";

    if (window.confirm(confirmMsg)) {
      let updatedFields;
      if (isSystemField) {
        const existing = fields.find(f => f.key === fieldKey);
        if (existing) {
          updatedFields = fields.map(f => f.key === fieldKey ? { ...f, isDeleted: true } : f);
        } else {
          updatedFields = [...fields, { key: fieldKey, isDeleted: true }];
        }
      } else {
        updatedFields = fields.filter(f => f.key !== fieldKey);
      }

      try {
        const payload = { fields: updatedFields, groups };
        await ApiClient.updateGlobalCustomFields(payload);
        setFields(updatedFields);
      } catch (err) {
        console.error("Error deleting field:", err);
        alert("Failed to delete field settings.");
      }
    }
  };

  const handleRestoreField = async (fieldKey) => {
    const updatedFields = fields.map(f => f.key === fieldKey ? { ...f, isDeleted: false } : f);
    try {
      const payload = { fields: updatedFields, groups };
      await ApiClient.updateGlobalCustomFields(payload);
      setFields(updatedFields);
    } catch (err) {
      console.error("Error restoring system field:", err);
      alert("Failed to restore field.");
    }
  };

  const toggleFieldExpand = (fieldKey) => {
    setExpandedFields(prev => ({ ...prev, [fieldKey]: !prev[fieldKey] }));
  };

  const getMergedFields = () => {
    const mergedSystemFields = SYSTEM_FIELDS.map(sf => {
      const override = fields.find(f => f.key === sf.key);
      if (override) {
        return { ...sf, ...override };
      }
      return sf;
    }).filter(f => !f.isDeleted);
    const customFields = fields.filter(f => !SYSTEM_FIELDS.some(sf => sf.key === f.key) && !f.isDeleted);
    return [...mergedSystemFields, ...customFields];
  };

  const renderFieldRow = (fd, level = 0) => {
    const isExpanded = !!expandedFields[fd.key];
    const Icon = getFieldTypeIcon(fd.type);
    const indentStyles = level === 1 ? { marginLeft: "24px", borderLeft: "2px solid rgba(131, 187, 225, 0.3)", paddingLeft: "16px", borderRadius: "0 8px 8px 0" } : {};
    return (
      <div className="field-def-row" key={fd.key} style={{
        background: "#ffffff",
        border: "1px solid rgba(131, 187, 225, 0.25)",
        borderRadius: "8px",
        overflow: "hidden",
        transition: "all 0.2s ease",
        opacity: fd.isDeleted ? 0.55 : 1,
        ...indentStyles
      }}>
        <div className="field-def-header" style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          cursor: "pointer",
          userSelect: "none"
        }} onClick={() => toggleFieldExpand(fd.key)}>
          <div className="field-def-left" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {fd.isSystem ? (
              <Lock size={14} style={{ color: "#94a3b8" }} />
            ) : (
              <GripVertical size={14} style={{ color: "#94a3b8", cursor: "grab" }} />
            )}
            <div className="field-def-icon" style={{
              color: "var(--grab-blue-dark)",
              background: "var(--grab-blue-light)",
              padding: "6px",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <Icon size={14} />
            </div>
            <div className="field-def-name-wrapper" style={{ display: "flex", flexDirection: "column", textAlign: "left" }}>
              <span className="field-def-name" style={{ fontSize: "13.5px", fontWeight: "600", color: "#0f172a" }}>{fd.name}</span>
              <span className="field-def-key" style={{ fontSize: "10.5px", color: "#64748b", fontFamily: "monospace" }}>{fd.key}</span>
            </div>
          </div>

          <div className="field-def-right" style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div className="field-def-badges" style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
              {fd.isSystem && <span className="badge-opt system" style={{
                fontSize: "9px",
                fontWeight: "600",
                padding: "2px 6px",
                borderRadius: "100px",
                textTransform: "uppercase",
                backgroundColor: "#e2e8f0",
                color: "#475569"
              }}>System</span>}
              {fd.isDeleted && <span className="badge-opt deleted" style={{
                fontSize: "9px",
                fontWeight: "600",
                padding: "2px 6px",
                borderRadius: "100px",
                textTransform: "uppercase",
                backgroundColor: "#fee2e2",
                color: "#ef4444"
              }}>Deleted</span>}
              <span style={{
                fontSize: "10px",
                fontWeight: "600",
                padding: "2px 6px",
                borderRadius: "100px",
                backgroundColor: "var(--grab-blue-light)",
                color: "var(--grab-blue-dark)"
              }}>{getFieldTypeLabel(fd.type)}</span>
              {fd.required && <span className="badge-opt required" style={{
                fontSize: "9px",
                fontWeight: "600",
                padding: "2px 6px",
                borderRadius: "100px",
                textTransform: "uppercase",
                backgroundColor: "#fee2e2",
                color: "#ef4444"
              }}>Required</span>}
              {fd.read_only && <span className="badge-opt readonly" style={{
                fontSize: "9px",
                fontWeight: "600",
                padding: "2px 6px",
                borderRadius: "100px",
                textTransform: "uppercase",
                backgroundColor: "#f1f5f9",
                color: "#64748b"
              }}>Read-Only</span>}
              {fd.quick_add && <span className="badge-opt quickadd" style={{
                fontSize: "9px",
                fontWeight: "600",
                padding: "2px 6px",
                borderRadius: "100px",
                textTransform: "uppercase",
                backgroundColor: "var(--grab-blue-light)",
                color: "var(--grab-blue-dark)"
              }}>Quick-Add</span>}
              {fd.unique && <span className="badge-opt unique" style={{
                fontSize: "9px",
                fontWeight: "600",
                padding: "2px 6px",
                borderRadius: "100px",
                textTransform: "uppercase",
                backgroundColor: "#fef3c7",
                color: "#d97706"
              }}>Unique</span>}
            </div>
            {isExpanded ? <ChevronUp size={14} style={{ color: "#94a3b8" }} /> : <ChevronDown size={14} style={{ color: "#94a3b8" }} />}
          </div>
        </div>

        {isExpanded && (
          <div className="field-def-details" style={{
            padding: "16px 20px",
            backgroundColor: "var(--grab-blue-bg)",
            borderTop: "1px solid rgba(131, 187, 225, 0.15)",
            display: "flex",
            flexDirection: "column",
            gap: "12px"
          }}>
            <div className="field-def-detail-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", textAlign: "left" }}>
              <div className="field-def-detail-item" style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <span className="field-def-detail-label" style={{ fontSize: "10.5px", color: "#64748b", fontWeight: "600", textTransform: "uppercase" }}>Description</span>
                <span className={`field-def-detail-value ${!fd.tooltip ? "empty" : ""}`} style={{
                  fontSize: "12.5px",
                  color: fd.tooltip ? "#334155" : "#94a3b8",
                  fontStyle: fd.tooltip ? "normal" : "italic"
                }}>
                  {fd.tooltip || "No tooltip text added"}
                </span>
              </div>
              <div className="field-def-detail-item" style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <span className="field-def-detail-label" style={{ fontSize: "10.5px", color: "#64748b", fontWeight: "600", textTransform: "uppercase" }}>Placeholder Text</span>
                <span className={`field-def-detail-value ${!fd.placeholder ? "empty" : ""}`} style={{
                  fontSize: "12.5px",
                  color: fd.placeholder ? "#334155" : "#94a3b8",
                  fontStyle: fd.placeholder ? "normal" : "italic"
                }}>
                  {fd.placeholder || "No custom placeholder"}
                </span>
              </div>
            </div>

            <div className="field-def-actions" style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "8px",
              borderTop: "1px dashed rgba(131, 187, 225, 0.2)",
              paddingTop: "12px",
              alignItems: "center"
            }}>
              {fd.isSystem && (
                <span style={{ fontSize: "11px", color: "#64748b", fontStyle: "italic", marginRight: "auto" }}>
                  System field
                </span>
              )}
              <>
                {fd.isDeleted ? (
                  <button className="btn-secondary" onClick={() => handleRestoreField(fd.key)} style={{ padding: "4px 8px", fontSize: "11.5px", gap: "4px", color: "#10b981", borderColor: "#10b981" }}>
                    <span>↩ Restore</span>
                  </button>
                ) : (
                  <>
                    <button className="btn-secondary" onClick={() => handleOpenEditDrawer(fd)} style={{ padding: "4px 8px", fontSize: "11.5px", gap: "4px" }}>
                      <Settings size={12} />
                      <span>Edit settings</span>
                    </button>
                    <button className="btn-danger-outline" onClick={() => handleDeleteField(fd.key)} style={{ padding: "4px 8px", fontSize: "11.5px", gap: "4px" }}>
                      <Trash2 size={12} />
                      <span>Delete</span>
                    </button>
                  </>
                )}
              </>
            </div>
          </div>
        )}
      </div>
    );
  };

  const getFieldTypeLabel = (typeId) => {
    const found = FIELD_TYPES.find(t => t.id === typeId);
    return found ? found.label : "Text field";
  };

  const getFieldTypeIcon = (typeId) => {
    const found = FIELD_TYPES.find(t => t.id === typeId);
    return found ? found.icon : Type;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full w-full text-slate-400 gap-4" style={{ height: "calc(100vh - 120px)" }}>
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-t-transparent" style={{ borderColor: "var(--grab-blue)", borderTopColor: "transparent" }} />
        <span className="text-sm font-medium">Loading fields schema...</span>
      </div>
    );
  }

  return (
    <div className="manage-fields-page-container">
      <style>{`
        .manage-fields-page-container {
          padding: 32px;
          font-family: 'Proxima Nova', 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          background-color: #ffffff;
          min-height: calc(100vh - 64px);
        }

        .page-header-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
        }

        .header-left {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .back-nav-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          background: none;
          border: none;
          color: #64748b;
          font-size: 13.5px;
          font-weight: 600;
          cursor: pointer;
          padding: 0;
          transition: color 0.2s ease;
        }

        .back-nav-btn:hover {
          color: var(--grab-blue-dark);
        }

        .page-title {
          font-size: 24px;
          font-weight: 700;
          color: #0f172a;
          margin: 4px 0 0 0;
        }

        .page-desc {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }

        /* Listing Grid */
        .fields-list-wrapper {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-top: 16px;
        }

        .empty-fields-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 24px;
          background: var(--grab-blue-bg);
          border: 1px dashed rgba(131, 187, 225, 0.4);
          border-radius: 12px;
          text-align: center;
          gap: 12px;
        }

        .empty-fields-title {
          font-size: 16px;
          font-weight: 600;
          color: #334155;
          margin: 0;
        }

        .empty-fields-desc {
          font-size: 13.5px;
          color: #64748b;
          max-width: 380px;
          margin: 0;
        }

        /* Slide-over Side Drawer Styling */
        .drawer-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(15, 23, 42, 0.4);
          backdrop-filter: blur(2px);
          z-index: 999;
          transition: opacity 0.3s ease;
        }

        .drawer-panel {
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          width: 50%;
          max-width: 650px;
          min-width: 450px;
          background-color: #ffffff;
          box-shadow: -10px 0 25px -5px rgba(0, 0, 0, 0.1), -10px 0 10px -5px rgba(0, 0, 0, 0.04);
          z-index: 1000;
          display: flex;
          flex-direction: column;
          transform: ${isDrawerOpen ? "translateX(0)" : "translateX(100%)"};
          transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .drawer-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          border-bottom: 1px solid #f1f5f9;
        }

        .drawer-title-wrapper {
          display: flex;
          flex-direction: column;
        }

        .drawer-title {
          font-size: 18px;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
        }

        .drawer-subtitle {
          font-size: 12.5px;
          color: #64748b;
          margin-top: 4px;
        }

        .drawer-close-btn {
          background: none;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          padding: 6px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s, color 0.2s;
        }

        .drawer-close-btn:hover {
          background-color: #f1f5f9;
          color: #475569;
        }

        .drawer-content {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
          display: flex;
          flex-direction: column;
        }

        .drawer-footer {
          padding: 16px 24px;
          border-top: 1px solid #f1f5f9;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          background-color: #fafafa;
        }

        /* 12-Card Grid Selection step */
        .grid-header-title {
          font-size: 14px;
          font-weight: 600;
          color: #334155;
          margin-bottom: 16px;
        }

        .field-cards-grid {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-bottom: 24px;
        }

        .field-type-card {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: flex-start;
          padding: 12px 16px;
          background: #ffffff;
          border: 1px solid rgba(131, 187, 225, 0.25);
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
          position: relative;
          gap: 14px;
        }

        .field-type-card:hover {
          border-color: var(--grab-blue);
          background-color: var(--grab-blue-light);
          transform: translateX(4px);
        }

        .field-type-card.selected {
          border-color: var(--grab-blue-dark);
          background-color: var(--grab-blue-light);
          box-shadow: 0 0 0 2px rgba(131, 187, 225, 0.3);
        }

        .card-icon-container {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--grab-blue-dark);
          background-color: var(--grab-blue-light);
          border-radius: 6px;
          margin: 0;
          font-weight: 700;
          font-size: 15px;
          flex-shrink: 0;
        }

        .field-type-card.selected .card-icon-container {
          background-color: var(--grab-blue);
          color: #ffffff;
        }

        .card-label {
          font-size: 13px;
          font-weight: 600;
          color: #1e293b;
          white-space: nowrap;
        }

        /* Form Configuration step styling */
        .type-indicator-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background-color: var(--grab-blue-bg);
          border: 1px solid rgba(131, 187, 225, 0.2);
          border-radius: 8px;
          margin-bottom: 24px;
        }

        .type-indicator-details {
          display: flex;
          flex-direction: column;
        }

        .type-indicator-title {
          font-size: 13px;
          font-weight: 700;
          color: var(--grab-blue-dark);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .type-indicator-desc {
          font-size: 12px;
          color: #64748b;
        }

        .form-group {
          margin-bottom: 16px;
          display: flex;
          flex-direction: column;
          gap: 6px;
          text-align: left;
        }

        .form-label {
          font-size: 12.5px;
          font-weight: 600;
          color: #334155;
        }

        .form-input {
          padding: 8px 12px;
          border: 1px solid rgba(131, 187, 225, 0.3);
          border-radius: 6px;
          font-size: 13.5px;
          outline: none;
          transition: border-color 0.2s;
        }

        .form-input:focus {
          border-color: var(--grab-blue);
        }

        .form-input:disabled {
          background-color: #f8fafc;
          color: #64748b;
          cursor: not-allowed;
        }

        .form-grid-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        /* Toggle Switches */
        .options-toggles-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-top: 12px;
          margin-bottom: 12px;
        }

        .switch-toggle-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #ffffff;
          border: 1px solid rgba(131, 187, 225, 0.15);
          border-radius: 8px;
          padding: 10px 14px;
          cursor: pointer;
          user-select: none;
          transition: all 0.2s ease;
        }

        .switch-toggle-item:hover {
          border-color: var(--grab-blue);
          background-color: var(--grab-blue-bg);
        }

        .switch-label-wrapper {
          display: flex;
          flex-direction: column;
          text-align: left;
        }

        .switch-title {
          font-size: 12.5px;
          font-weight: 600;
          color: #334155;
        }

        .switch-desc {
          font-size: 11px;
          color: #64748b;
        }

        .toggle-switch-ios {
          position: relative;
          width: 36px;
          height: 20px;
          background-color: #cbd5e1;
          border-radius: 100px;
          transition: background-color 0.2s ease;
          flex-shrink: 0;
        }

        .toggle-switch-ios::after {
          content: "";
          position: absolute;
          top: 2px;
          left: 2px;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background-color: #ffffff;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
          transition: transform 0.2s ease;
        }

        .switch-toggle-item input {
          display: none;
        }

        .switch-toggle-item input:checked + .toggle-switch-ios {
          background-color: var(--grab-blue-dark);
        }

        .switch-toggle-item input:checked + .toggle-switch-ios::after {
          transform: translateX(16px);
        }

        /* Buttons styles */
        .btn-primary {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: var(--grab-blue-dark);
          border: none;
          border-radius: 6px;
          font-size: 13.5px;
          font-weight: 600;
          color: #ffffff;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-primary:hover {
          background: var(--grab-blue);
        }

        .btn-primary:disabled {
          background-color: #cbd5e1;
          color: #94a3b8;
          cursor: not-allowed;
        }

        .btn-secondary {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: #ffffff;
          border: 1px solid rgba(131, 187, 225, 0.3);
          border-radius: 6px;
          font-size: 13.5px;
          font-weight: 500;
          color: #475569;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-secondary:hover {
          background-color: var(--grab-blue-light);
          border-color: var(--grab-blue);
        }

        .btn-danger-outline {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: #ffffff;
          border: 1px solid rgba(239, 68, 68, 0.2);
          border-radius: 6px;
          font-size: 12.5px;
          font-weight: 600;
          color: #ef4444;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-danger-outline:hover {
          background-color: #fee2e2;
          border-color: #ef4444;
        }
      `}</style>

      {/* Backdrop overlay */}
      {isDrawerOpen && <div className="drawer-backdrop" onClick={handleCloseDrawer} />}

      {/* Slide-over Side Drawer panel */}
      <div className="drawer-panel" style={{ transform: isDrawerOpen ? "translateX(0)" : "translateX(100%)" }}>
        <div className="drawer-header">
          <div className="drawer-title-wrapper">
            <h3 className="drawer-title" style={{ textAlign: "left" }}>
              {drawerType === "group"
                ? (formGroupId ? "Edit Group Settings" : "Add Group")
                : (formFieldId ? "Edit Custom Field Settings" : drawerStep === 1 ? "Select a custom field" : "Configure custom field")}
            </h3>
            <span className="drawer-subtitle" style={{ textAlign: "left" }}>
              {drawerType === "group"
                ? "Organize fields under structured categories"
                : (formFieldId ? "Modify options, placeholders, and restrictions" : drawerStep === 1 ? "Select a type of parameter attributes to hold values" : "Define options, labels, and parameters")}
            </span>
          </div>
          <button className="drawer-close-btn" onClick={handleCloseDrawer}>
            <X size={16} />
          </button>
        </div>

        <div className="drawer-content custom-scrollbar">
          {drawerType === "group" ? (
            <form onSubmit={handleSaveGroup} style={{ flex: 1, display: "flex", flexDirection: "column", gap: "20px" }}>
              <div className="form-group">
                <label className="form-label">Group name *</label>
                <input
                  type="text"
                  placeholder="Enter a name (e.g. Lead details)"
                  className="form-input"
                  required
                  value={formGroupName}
                  onChange={(e) => handleGroupNameChange(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Internal name *</label>
                <input
                  type="text"
                  className="form-input"
                  disabled={formGroupId === "ec_basic_information" || formGroupId === "ec_telephone_numbers"}
                  value={formGroupKey}
                  onChange={(e) => setFormGroupKey(e.target.value)}
                />
              </div>

              <div className="form-group" style={{ flexDirection: "row", alignItems: "center", gap: "8px" }}>
                <input
                  type="checkbox"
                  id="formGroupNest"
                  checked={formGroupNest}
                  onChange={(e) => {
                    setFormGroupNest(e.target.checked);
                    if (e.target.checked && !formGroupParent) {
                      const topGroups = groups.filter(g => !g.parentGroup && g.key !== formGroupId);
                      if (topGroups.length > 0) setFormGroupParent(topGroups[0].key);
                    }
                  }}
                />
                <label htmlFor="formGroupNest" className="form-label" style={{ margin: 0, cursor: "pointer" }}>
                  Associate with the parent group
                </label>
              </div>

              {formGroupNest && (
                <div className="form-group">
                  <label className="form-label">Parent Group</label>
                  <select
                    className="form-input"
                    value={formGroupParent}
                    onChange={(e) => setFormGroupParent(e.target.value)}
                    style={{ height: "37.5px", background: "white" }}
                  >
                    {groups.filter(g => !g.parentGroup && g.key !== formGroupId).map(g => (
                      <option key={g.key} value={g.key}>{g.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </form>
          ) : drawerStep === 1 ? (
            <div>
              <div className="field-cards-grid">
                {FIELD_TYPES.map((type) => {
                  const Icon = type.icon;
                  const isSelected = selectedType === type.id;
                  return (
                    <div
                      key={type.id}
                      className={`field-type-card ${isSelected ? "selected" : ""}`}
                      onClick={() => handleCardSelect(type.id)}
                    >
                      <div className="card-icon-container">
                        <Icon size={18} />
                      </div>
                      <span className="card-label">{type.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (() => {
            const isSystemField = SYSTEM_FIELDS.some(sf => sf.key === formFieldId);
            return (
              <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <div className="type-indicator-row">
                  <div className="card-icon-container" style={{ margin: 0 }}>
                    {React.createElement(getFieldTypeIcon(selectedType), { size: 16 })}
                  </div>
                  <div className="type-indicator-details" style={{ textAlign: "left" }}>
                    <span className="type-indicator-title">{getFieldTypeLabel(selectedType)}</span>
                    <span className="type-indicator-desc">
                      {FIELD_TYPES.find(t => t.id === selectedType)?.desc || ""}
                    </span>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Field Label *</label>
                  <input
                    type="text"
                    placeholder="e.g. Student ID"
                    className="form-input"
                    required
                    value={formLabel}
                    onChange={(e) => handleLabelChange(e.target.value)}
                  />
                </div>

                <div className="form-grid-2">
                  <div className="form-group">
                    <label className="form-label">Internal Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      disabled={isSystemField}
                      value={formKey}
                      onChange={(e) => setFormKey(e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Field Type</label>
                    <select
                      value={selectedType || "text"}
                      onChange={(e) => setSelectedType(e.target.value)}
                      className="form-input"
                      style={{ height: "37.5px", background: "white" }}
                      disabled={isSystemField}
                    >
                      {FIELD_TYPES.map(t => (
                        <option key={t.id} value={t.id}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Category Group</label>
                  <select
                    className="form-input"
                    value={formFieldGroup}
                    onChange={(e) => setFormFieldGroup(e.target.value)}
                    style={{ height: "37.5px", background: "white" }}
                  >
                    {groups.map(g => (
                      <option key={g.key} value={g.key}>
                        {g.parentGroup ? `└─ ${g.name}` : g.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input
                    type="text"
                    placeholder="Describe the purpose of this field (shows on hover)"
                    className="form-input"
                    value={formTooltip}
                    onChange={(e) => setFormTooltip(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Placeholder Text</label>
                  <input
                    type="text"
                    placeholder="Start typing..."
                    className="form-input"
                    value={formPlaceholder}
                    onChange={(e) => setFormPlaceholder(e.target.value)}
                  />
                </div>

                {/* Type-Specific Configurations */}
                {selectedType === "number" && (
                  <div className="form-group">
                    <label className="form-label">Number Format</label>
                    <select
                      value={formNumberFormat}
                      onChange={(e) => setFormNumberFormat(e.target.value)}
                      className="form-input"
                      style={{ height: "37.5px", background: "white" }}
                    >
                      <option value="integer">Integer (e.g. 100)</option>
                      <option value="decimal">Decimal (e.g. 99.99)</option>
                      <option value="percent">Percent (e.g. 75%)</option>
                      <option value="currency">Currency (e.g. $1,234.56)</option>
                    </select>
                  </div>
                )}

                {selectedType === "lookup" && (
                  <div className="form-group">
                    <label className="form-label">Source Module</label>
                    <select
                      value={formLookupModule}
                      onChange={(e) => setFormLookupModule(e.target.value)}
                      className="form-input"
                      style={{ height: "37.5px", background: "white" }}
                    >
                      <option value="Contacts">Contacts</option>
                      <option value="Companies">Companies</option>
                      <option value="Deals">Deals</option>
                      <option value="Leads">Leads</option>
                      <option value="Users">Users</option>
                    </select>
                  </div>
                )}

                {["dropdown", "multiselect", "radio"].includes(selectedType) && (
                  <div className="form-group" style={{ marginTop: "12px", borderTop: "1px dashed rgba(131, 187, 225, 0.2)", paddingTop: "12px" }}>
                    <label className="form-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>Choices List ({formChoices.length})</span>
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ padding: "2px 8px", fontSize: "11px", height: "24px" }}
                        onClick={() => setShowBulkAddModal(!showBulkAddModal)}
                      >
                        {showBulkAddModal ? "Hide Bulk Add" : "Bulk Add"}
                      </button>
                    </label>

                    {showBulkAddModal && (
                      <div style={{
                        border: "1px solid var(--grab-blue)",
                        borderRadius: "8px",
                        padding: "12px",
                        backgroundColor: "#ffffff",
                        marginTop: "8px",
                        marginBottom: "12px"
                      }}>
                        <label className="form-label">Bulk Add Options (one per line)</label>
                        <textarea
                          value={bulkChoicesText}
                          onChange={(e) => setBulkChoicesText(e.target.value)}
                          className="form-input"
                          style={{ width: "100%", height: "120px", fontFamily: "monospace", fontSize: "12px", resize: "vertical" }}
                          placeholder={`Option 1\nOption 2\nOption 3`}
                        />
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px" }}>
                          <button
                            type="button"
                            className="btn-secondary"
                            style={{ padding: "4px 10px", fontSize: "12px" }}
                            onClick={() => {
                              setShowBulkAddModal(false);
                              setBulkChoicesText("");
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn-primary"
                            style={{ padding: "4px 10px", fontSize: "12px" }}
                            onClick={() => {
                              const splitChoices = bulkChoicesText
                                .split("\n")
                                .map(c => c.trim())
                                .filter(c => c.length > 0);

                              const merged = [...formChoices];
                              splitChoices.forEach(c => {
                                if (!merged.includes(c)) merged.push(c);
                              });

                              setFormChoices(merged);
                              setBulkChoicesText("");
                              setShowBulkAddModal(false);
                            }}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Search & Sort actions */}
                    <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                      <div style={{ position: "relative", flex: 1 }}>
                        <input
                          type="text"
                          placeholder="Search choices..."
                          value={choiceSearchQuery}
                          onChange={(e) => setChoiceSearchQuery(e.target.value)}
                          className="form-input"
                          style={{ width: "100%", paddingLeft: "28px", height: "32px", fontSize: "12px" }}
                        />
                        <span style={{ position: "absolute", left: "8px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", display: "flex", alignItems: "center" }}>
                          <Search size={12} />
                        </span>
                      </div>
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ height: "32px", fontSize: "12px", padding: "0 10px" }}
                        onClick={() => {
                          const sorted = [...formChoices].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
                          setFormChoices(sorted);
                        }}
                      >
                        Sort A-Z
                      </button>
                    </div>

                    {/* Add single choice inline */}
                    <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                      <input
                        type="text"
                        placeholder="Enter option..."
                        value={newChoiceText}
                        onChange={(e) => setNewChoiceText(e.target.value)}
                        className="form-input"
                        style={{ flex: 1, height: "32px", fontSize: "12px" }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const trimmed = newChoiceText.trim();
                            if (trimmed && !formChoices.includes(trimmed)) {
                              setFormChoices([...formChoices, trimmed]);
                              setNewChoiceText("");
                            }
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="btn-primary"
                        style={{ height: "32px", fontSize: "12px", padding: "0 12px" }}
                        onClick={() => {
                          const trimmed = newChoiceText.trim();
                          if (trimmed && !formChoices.includes(trimmed)) {
                            setFormChoices([...formChoices, trimmed]);
                            setNewChoiceText("");
                          }
                        }}
                      >
                        Add
                      </button>
                    </div>

                    {/* Choices List Scrollable Container */}
                    <div style={{
                      border: "1px solid rgba(131, 187, 225, 0.2)",
                      borderRadius: "6px",
                      maxHeight: "180px",
                      overflowY: "auto",
                      backgroundColor: "#f8fafc",
                      padding: "6px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px"
                    }}>
                      {formChoices
                        .filter(c => c.toLowerCase().includes(choiceSearchQuery.toLowerCase()))
                        .map((choice, index) => (
                          <div key={`${choice}-${index}`} style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "6px 8px",
                            backgroundColor: "#ffffff",
                            border: "1px solid rgba(131, 187, 225, 0.1)",
                            borderRadius: "4px"
                          }}>
                            <span style={{ fontSize: "12.5px", color: "#334155" }}>{choice}</span>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <button
                                type="button"
                                disabled={index === 0}
                                style={{ border: "none", background: "none", cursor: index === 0 ? "default" : "pointer", opacity: index === 0 ? 0.3 : 1, color: "#64748b", padding: "0 2px" }}
                                onClick={() => {
                                  const updated = [...formChoices];
                                  const temp = updated[index];
                                  updated[index] = updated[index - 1];
                                  updated[index - 1] = temp;
                                  setFormChoices(updated);
                                }}
                              >
                                ▲
                              </button>
                              <button
                                type="button"
                                disabled={index === formChoices.length - 1}
                                style={{ border: "none", background: "none", cursor: index === formChoices.length - 1 ? "default" : "pointer", opacity: index === formChoices.length - 1 ? 0.3 : 1, color: "#64748b", padding: "0 2px" }}
                                onClick={() => {
                                  const updated = [...formChoices];
                                  const temp = updated[index];
                                  updated[index] = updated[index + 1];
                                  updated[index + 1] = temp;
                                  setFormChoices(updated);
                                }}
                              >
                                ▼
                              </button>
                              <button
                                type="button"
                                style={{ border: "none", background: "none", cursor: "pointer", color: "#ef4444", fontSize: "11px", fontWeight: "bold", padding: "0 4px" }}
                                onClick={() => {
                                  setFormChoices(formChoices.filter((_, idx) => idx !== index));
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ))}
                      {formChoices.filter(c => c.toLowerCase().includes(choiceSearchQuery.toLowerCase())).length === 0 && (
                        <span style={{ fontSize: "11.5px", color: "#94a3b8", fontStyle: "italic", textAlign: "center", padding: "10px" }}>
                          No choices found
                        </span>
                      )}
                    </div>
                  </div>
                )}


                <div className="options-toggles-grid">
                  <label className="switch-toggle-item">
                    <div className="switch-label-wrapper">
                      <span className="switch-title">Required field</span>
                      <span className="switch-desc">Enforce entry validation</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={formRequired}
                      onChange={(e) => setFormRequired(e.target.checked)}
                    />
                    <div className="toggle-switch-ios" />
                  </label>

                  <label className="switch-toggle-item">
                    <div className="switch-label-wrapper">
                      <span className="switch-title">Quick-add view</span>
                      <span className="switch-desc">Display in creation shortcuts</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={formQuickAdd}
                      onChange={(e) => setFormQuickAdd(e.target.checked)}
                    />
                    <div className="toggle-switch-ios" />
                  </label>

                  <label className="switch-toggle-item">
                    <div className="switch-label-wrapper">
                      <span className="switch-title">Read-only field</span>
                      <span className="switch-desc">Block profile edits</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={formReadOnly}
                      onChange={(e) => setFormReadOnly(e.target.checked)}
                    />
                    <div className="toggle-switch-ios" />
                  </label>

                  <label className="switch-toggle-item">
                    <div className="switch-label-wrapper">
                      <span className="switch-title">Unique value</span>
                      <span className="switch-desc">Prevent duplicates</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={formUnique}
                      onChange={(e) => setFormUnique(e.target.checked)}
                    />
                    <div className="toggle-switch-ios" />
                  </label>
                </div>
              </div>
            );
          })()}
        </div>

        <div className="drawer-footer">
          {drawerType === "group" ? (
            <>
              <button className="btn-secondary" onClick={handleCloseDrawer}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSaveGroup}>
                Save Group
              </button>
            </>
          ) : drawerStep === 1 ? (
            <>
              <button className="btn-secondary" onClick={handleCloseDrawer}>
                Cancel
              </button>
              <button
                className="btn-primary"
                disabled={!selectedType}
                onClick={handleProceedToForm}
              >
                Add selected
              </button>
            </>
          ) : (
            <>
              {!formFieldId && (
                <button className="btn-secondary" style={{ marginRight: "auto" }} onClick={() => setDrawerStep(1)}>
                  Back
                </button>
              )}
              <button className="btn-secondary" onClick={handleCloseDrawer}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSaveField}>
                Save Field
              </button>
            </>
          )}
        </div>
      </div>

      {/* Main Page Layout */}
      <div className="page-header-row">
        <div className="header-left" style={{ textAlign: "left" }}>
          <button 
            className="back-nav-btn" 
            onClick={() => {
              if (backRefContactId) {
                navigate(`/contacts/${backRefContactId}?tab=details`);
              } else {
                navigate("/contacts");
              }
            }}
          >
            <ArrowLeft size={14} />
            <span>{backRefContactId ? "Back to Contact" : "Back to Contacts"}</span>
          </button>
          <h1 className="page-title">Manage Custom Fields</h1>
          <p className="page-desc">
            Declare global contact attributes available across all contact profiles in EasyChat.
          </p>
        </div>

        <div style={{ display: "flex", gap: "8px" }}>
          <button className="btn-primary" style={{ gap: "6px" }} onClick={() => handleOpenAddDrawer()}>
            <Plus size={16} />
            <span>Add field</span>
          </button>
          <button className="btn-secondary" style={{ gap: "6px" }} onClick={() => handleOpenAddGroupDrawer()}>
            <Plus size={16} />
            <span>Add group</span>
          </button>
        </div>
      </div>

      <div className="fields-list-wrapper">
        {(() => {
          const allFields = getMergedFields();
          const ungroupedFields = allFields.filter(f => !groups.some(g => g.key === f.group));
          const topLevelGroups = groups.filter(g => !g.parentGroup);

          return (
            <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
              {topLevelGroups.map((group) => {
                const subGroups = groups.filter(g => g.parentGroup === group.key);
                const directFields = allFields.filter(f => f.group === group.key);

                return (
                  <div key={group.key} className="group-container" style={{
                    border: "none",
                    borderRadius: "0",
                    background: "none",
                    padding: "12px 0",
                    textAlign: "left"
                  }}>
                    {/* Top Group Header */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ color: "#3b82f6", display: "flex", alignItems: "center" }}><Folder size={18} /></span>
                        <span style={{ fontSize: "16px", fontWeight: "700", color: "#1e293b" }}>{group.name}</span>
                        <button className="group-edit-btn" onClick={() => handleOpenEditGroupDrawer(group)} style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", padding: "2px" }} title="Edit group name">
                          <Settings size={14} />
                        </button>
                        <button className="group-delete-btn" onClick={() => handleDeleteGroup(group.key)} style={{ background: "none", border: "none", color: "#f87171", cursor: "pointer", padding: "2px" }} title="Delete group">
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button className="btn-secondary" style={{ padding: "4px 10px", fontSize: "12px", height: "28px" }} onClick={() => handleOpenAddDrawer(group.key)}>
                          <Plus size={12} /> Add field
                        </button>
                        <button className="btn-secondary" style={{ padding: "4px 10px", fontSize: "12px", height: "28px" }} onClick={() => handleOpenAddGroupDrawer(group.key)}>
                          <Plus size={12} /> Add group
                        </button>
                      </div>
                    </div>

                    {/* Direct Fields List */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px", paddingLeft: "12px" }}>
                      {directFields.map(fd => renderFieldRow(fd, 0))}
                    </div>

                    {/* Nested Subgroups */}
                    {subGroups.map((subGroup) => {
                      const subFields = allFields.filter(f => f.group === subGroup.key);
                      return (
                        <div key={subGroup.key} className="subgroup-container" style={{
                          marginTop: "16px",
                          marginLeft: "12px",
                          padding: "12px 0 0 12px",
                          borderRadius: "0",
                          background: "none",
                          border: "none",
                          borderLeft: "2px solid rgba(131, 187, 225, 0.3)"
                        }}>
                          {/* Subgroup Header */}
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <span style={{ color: "#f59e0b", display: "flex", alignItems: "center" }}><Folder size={16} /></span>
                              <span style={{ fontSize: "14px", fontWeight: "600", color: "#334155" }}>{subGroup.name}</span>
                              <button className="group-edit-btn" onClick={() => handleOpenEditGroupDrawer(subGroup)} style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", padding: "2px" }}>
                                <Settings size={12} />
                              </button>
                              <button className="group-delete-btn" onClick={() => handleDeleteGroup(subGroup.key)} style={{ background: "none", border: "none", color: "#f87171", cursor: "pointer", padding: "2px" }}>
                                <Trash2 size={12} />
                              </button>
                            </div>
                            <button className="btn-secondary" style={{ padding: "4px 8px", fontSize: "11px", height: "24px" }} onClick={() => handleOpenAddDrawer(subGroup.key)}>
                              <Plus size={10} /> Add field
                            </button>
                          </div>

                          {/* Subgroup Fields */}
                          <div style={{ display: "flex", flexDirection: "column", gap: "10px", paddingLeft: "12px" }}>
                            {subFields.map(fd => renderFieldRow(fd, 1))}
                            {subFields.length === 0 && (
                              <div className="empty-fields-placeholder" onClick={() => handleOpenAddDrawer(subGroup.key)} style={{
                                border: "1px dashed rgba(131, 187, 225, 0.3)",
                                padding: "12px",
                                borderRadius: "6px",
                                textAlign: "center",
                                color: "#94a3b8",
                                fontSize: "12px",
                                cursor: "pointer"
                              }}>
                                + Click to add fields here
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {directFields.length === 0 && subGroups.length === 0 && (
                      <div className="empty-fields-placeholder" onClick={() => handleOpenAddDrawer(group.key)} style={{
                        border: "1px dashed rgba(131, 187, 225, 0.3)",
                        padding: "16px",
                        borderRadius: "8px",
                        textAlign: "center",
                        color: "#94a3b8",
                        fontSize: "13px",
                        cursor: "pointer",
                        marginLeft: "12px",
                        marginTop: "8px"
                      }}>
                        + Click to add fields here
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Ungrouped Fields Section */}
              {ungroupedFields.length > 0 && (
                <div className="group-container" style={{
                  border: "none",
                  borderRadius: "0",
                  background: "none",
                  padding: "12px 0",
                  textAlign: "left"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
                    <span style={{ fontSize: "16px", fontWeight: "700", color: "#64748b" }}>Ungrouped Fields</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {ungroupedFields.map(fd => renderFieldRow(fd, 0))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
